import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Search,
  Filter,
  Eye,
  Check,
  ChevronDown,
  ChevronRight,
  FileText,
  Loader2,
  Plus,
  RefreshCw,
  RotateCcw,
  ExternalLink,
  Tag,
  Download,
} from 'lucide-react';
import { Button } from '@components/Button';
import { Card, CardContent } from '@components/Card';
import { TableHeader, TableRow, TableCell } from '@components/Table';
import { PermissionGuard } from '@components/PermissionGuard';
import { DateInputBR } from '@components/DateInputBR';
import { invoiceService } from '../../services/invoiceService';
import { invoiceTransactionTypeService } from '../../services/invoiceTransactionTypeService';
import { companyService } from '../../services/companyService';
import { userPreferencesService } from '../../services/userPreferencesService';
import { Company } from '../../types/company';
import {
  Invoice,
  InvoiceItem,
  InvoiceSearchModel,
  INVOICE_STATUS_OPTIONS,
  INVOICE_MODEL_OPTIONS,
  getInvoiceModelLabel,
} from '../../types/invoice';
import { InvoiceTransactionType } from '../../types/invoiceTransactionType';
import { formatCnpj } from '../../types/company';
import { ReturnInvoiceModal } from '@components/ReturnInvoiceModal';

// ─── Helpers ─────────────────────────────────────────────────────

const formatCurrency = (value?: number) =>
  value !== undefined && value !== null
    ? value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    : '—';

const formatDate = (dateStr?: string) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr + (dateStr.length === 10 ? 'T00:00:00' : ''));
  return d.toLocaleDateString('pt-BR');
};

// Status badge colors
const STATUS_BADGE_CLASSES: Record<number, string> = {
  1:  'bg-yellow-100 text-yellow-800',    // Pendente
  2:  'bg-red-100 text-red-700',          // Cancelada
  3:  'bg-orange-100 text-orange-700',    // Aguardando recibo
  4:  'bg-red-200 text-red-800',          // Rejeitada
  5:  'bg-green-100 text-green-700',      // Autorizada
  6:  'bg-emerald-100 text-emerald-700',  // Emitida DANFE
  7:  'bg-blue-100 text-blue-700',        // Registrada
  8:  'bg-orange-200 text-orange-800',    // Aguardando protocolo
  9:  'bg-slate-200 text-slate-700',      // Denegada
  10: 'bg-cyan-100 text-cyan-700',        // Consulta situação
  11: 'bg-purple-100 text-purple-700',    // Bloqueada
};

const getOperationTypeBadge = (type?: string) => {
  if (!type) return <span className="text-slate-400">—</span>;
  if (type.toUpperCase() === 'E')
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
        Entrada
      </span>
    );
  if (type.toUpperCase() === 'S')
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
        Saída
      </span>
    );
  return <span className="text-slate-500 text-xs">{type}</span>;
};

const getStatusBadge = (status?: number) => {
  if (status === undefined || status === null) return <span className="text-slate-500">—</span>;
  const opt = INVOICE_STATUS_OPTIONS.find(o => o.value === status);
  const label = opt?.label ?? `Status ${status}`;
  const cls = STATUS_BADGE_CLASSES[status] ?? 'bg-slate-100 text-slate-700';
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      {label}
    </span>
  );
};

// Default date range: last 3 months
const getDefault3MonthsAgo = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 3);
  return d.toISOString().split('T')[0];
};
const getToday = () => new Date().toISOString().split('T')[0];

const STORAGE_KEY = 'invoices-list-state';

const readSaved = () => {
  try {
    const s = sessionStorage.getItem(STORAGE_KEY);
    return s ? JSON.parse(s) : null;
  } catch { return null; }
};

const getPageRange = (current: number, total: number): (number | '...')[] => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | '...')[] = [1];
  if (current > 3) pages.push('...');
  for (let p = Math.max(2, current - 1); p <= Math.min(total - 1, current + 1); p++) {
    pages.push(p);
  }
  if (current < total - 2) pages.push('...');
  pages.push(total);
  return pages;
};

// ─── Component ───────────────────────────────────────────────────

export const InvoiceListView: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as { successMessage?: string; refresh?: boolean } | null;
  const [successMessage, setSuccessMessage] = useState<string | null>(
    locationState?.successMessage ?? null
  );

  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [customerSearch, setCustomerSearch] = useState<string>(() => readSaved()?.customerSearch ?? '');
  const [searchTerm, setSearchTerm] = useState<string>(() => readSaved()?.customerSearch ?? '');
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Filters
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<{
    company_id: number | undefined;
    status_ids: number[];
    invoice_transaction_type_ids: number[];
    model: number | undefined;
    date_from: string;
    date_to: string;
  }>(() => {
    const saved = readSaved()?.filters;
    return {
      company_id: saved?.company_id ?? undefined,
      status_ids: Array.isArray(saved?.status_ids) ? saved.status_ids : [],
      invoice_transaction_type_ids: Array.isArray(saved?.invoice_transaction_type_ids) ? saved.invoice_transaction_type_ids : [],
      model: saved?.model ?? undefined,
      date_from: saved?.date_from ?? getDefault3MonthsAgo(),
      date_to: saved?.date_to ?? getToday(),
    };
  });
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);

  // Lookups
  const [companies, setCompanies] = useState<Company[]>([]);
  const [transactionTypes, setTransactionTypes] = useState<InvoiceTransactionType[]>([]);

  // Pagination
  const [currentPage, setCurrentPage] = useState<number>(() => readSaved()?.currentPage ?? 1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalInvoices, setTotalInvoices] = useState(0);
  const pageSize = 20;

  // Expanded items toggle + per-invoice items cache
  const [expandedInvoiceId, setExpandedInvoiceId] = useState<number | null>(null);
  const [invoiceItemsCache, setInvoiceItemsCache] = useState<Record<number, { loading: boolean; items: InvoiceItem[] }>>({});

  // Quick navigation
  const [quickNavValue, setQuickNavValue] = useState('');
  const [quickNavCompanyId, setQuickNavCompanyId] = useState<number | undefined>(undefined);

  // XML export
  const [xmlExporting, setXmlExporting] = useState(false);
  const [totalAmountSum, setTotalAmountSum] = useState<number | undefined>(undefined);
  const [quickNavLoading, setQuickNavLoading] = useState(false);
  const [quickNavError, setQuickNavError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Return invoice modal
  const [returnInvoiceModalOpen, setReturnInvoiceModalOpen] = useState(false);
  const [returnInvoiceSource, setReturnInvoiceSource] = useState<Invoice | undefined>(undefined);

  // ── Load ──────────────────────────────────────────────────────

  // When navigating back from create/edit with refresh:true, reload once. A nota agora é
  // gravada em Financial.Invoice na própria emissão (SEFAZ direto), então não há mais espera
  // por webhook — basta recarregar imediatamente (o loadInvoices padrão já roda no mount).
  useEffect(() => {
    if (locationState?.refresh) {
      // Clear the state so a future visit doesn't retrigger
      navigate(location.pathname, { replace: true, state: { successMessage: locationState.successMessage } });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadInvoices = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const searchFilters: InvoiceSearchModel = {
        page: currentPage,
        pageSize,
        company_id: filters.company_id,
        status_ids: filters.status_ids.length > 0 ? filters.status_ids : undefined,
        invoice_transaction_type_ids: filters.invoice_transaction_type_ids.length > 0 ? filters.invoice_transaction_type_ids : undefined,
        model: filters.model,
        date_from: filters.date_from || undefined,
        date_to: filters.date_to || undefined,
        customer_name: searchTerm.trim() || undefined,
      };

      const response = await invoiceService.getInvoicesSales(searchFilters);
      const data = Array.isArray(response.data) ? response.data : [];
      setInvoices(data);
      setTotalInvoices(response.pagination?.total || data.length);
      setTotalPages(response.pagination?.totalPages || 1);
      setTotalAmountSum(response.summary?.total_amount_sum);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar notas fiscais');
    } finally {
      setLoading(false);
    }
  }, [currentPage, filters, searchTerm]);

  const handleManualRefresh = async () => {
    setRefreshing(true);
    await loadInvoices();
    setRefreshing(false);
  };

  // ── Open return invoice modal for a specific sales invoice ──
  const handleOpenReturnInvoice = (invoice: Invoice) => {
    setReturnInvoiceSource(invoice);
    setReturnInvoiceModalOpen(true);
  };

  const handleReturnInvoiceSuccess = (message: string) => {
    setReturnInvoiceModalOpen(false);
    setReturnInvoiceSource(undefined);
    setSuccessMessage(message);
    // Reload to show the new return invoice linkage
    loadInvoices();
  };

  useEffect(() => { loadInvoices(); }, [loadInvoices]);

  // Persist filter state
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ filters, customerSearch, currentPage }));
    } catch {
      // Ignorado de propósito: guardar o filtro é conveniência. Em aba anônima
      // ou com a cota estourada, o sessionStorage lança — e perder o filtro não
      // pode quebrar a listagem.
    }
  }, [filters, customerSearch, currentPage]);

  // Save preferences when filters change (debounced via loadInvoices trigger)
  useEffect(() => {
    if (!preferencesLoaded) return;
    userPreferencesService.savePreferences('FISCAL_INVOICE_LIST', {
      status_ids: filters.status_ids,
      invoice_transaction_type_ids: filters.invoice_transaction_type_ids,
      company_id: filters.company_id,
      model: filters.model,
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status_ids, filters.invoice_transaction_type_ids, filters.company_id, filters.model, preferencesLoaded]);

  useEffect(() => {
    companyService.getCompanies().then(setCompanies).catch(() => {});
    invoiceTransactionTypeService.getActiveSales().then(setTransactionTypes).catch(() => {});
    // Load user preferences
    interface InvoiceListPrefs {
      status_ids?: number[];
      invoice_transaction_type_ids?: number[];
      company_id?: number;
      model?: number;
    }
    userPreferencesService.getPreferences<InvoiceListPrefs>('FISCAL_INVOICE_LIST').then(prefs => {
      if (prefs) {
        setFilters(prev => ({
          ...prev,
          ...(prefs.status_ids && prefs.status_ids.length > 0 ? { status_ids: prefs.status_ids } : {}),
          ...(prefs.invoice_transaction_type_ids && prefs.invoice_transaction_type_ids.length > 0 ? { invoice_transaction_type_ids: prefs.invoice_transaction_type_ids } : {}),
          ...(prefs.company_id !== undefined ? { company_id: prefs.company_id } : {}),
          ...(prefs.model !== undefined ? { model: prefs.model } : {}),
        }));
      }
      setPreferencesLoaded(true);
    }).catch(() => setPreferencesLoaded(true));
  }, []);

  const handleSearchChange = (value: string) => {
    setCustomerSearch(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setCurrentPage(1);
      setSearchTerm(value);
    }, 500);
  };

  // Filter dropdown outside-click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.filter-dropdown') && !target.closest('[data-filter-trigger]')) {
        setShowFilters(false);
      }
    };
    if (showFilters) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showFilters]);

  // ── Active filter count ───────────────────────────────────────

  const activeFilterCount = [
    filters.company_id !== undefined,
    filters.status_ids.length > 0,
    filters.invoice_transaction_type_ids.length > 0,
    filters.model !== undefined,
    filters.date_from !== getDefault3MonthsAgo(),
    filters.date_to !== getToday(),
  ].filter(Boolean).length;

  // ── Status toggle helpers ─────────────────────────────────────

  const toggleStatus = (statusId: number) => {
    setFilters(prev => ({
      ...prev,
      status_ids: prev.status_ids.includes(statusId)
        ? prev.status_ids.filter(s => s !== statusId)
        : [...prev.status_ids, statusId],
    }));
    setCurrentPage(1);
  };

  const toggleAllStatuses = () => {
    if (filters.status_ids.length === INVOICE_STATUS_OPTIONS.length) {
      setFilters(prev => ({ ...prev, status_ids: [] }));
    } else {
      setFilters(prev => ({ ...prev, status_ids: INVOICE_STATUS_OPTIONS.map(o => o.value) }));
    }
    setCurrentPage(1);
  };

  // ── Transaction type toggle helpers ───────────────────────────

  const toggleTransactionType = (id: number) => {
    setFilters(prev => ({
      ...prev,
      invoice_transaction_type_ids: prev.invoice_transaction_type_ids.includes(id)
        ? prev.invoice_transaction_type_ids.filter(t => t !== id)
        : [...prev.invoice_transaction_type_ids, id],
    }));
    setCurrentPage(1);
  };

  const toggleAllTransactionTypes = () => {
    if (filters.invoice_transaction_type_ids.length === transactionTypes.length) {
      setFilters(prev => ({ ...prev, invoice_transaction_type_ids: [] }));
    } else {
      setFilters(prev => ({ ...prev, invoice_transaction_type_ids: transactionTypes.map(t => t.invoice_transaction_type_id) }));
    }
    setCurrentPage(1);
  };

  // ── Toggle expand ─────────────────────────────────────────────

  const toggleExpand = async (invoiceId: number) => {
    if (expandedInvoiceId === invoiceId) {
      setExpandedInvoiceId(null);
      return;
    }
    setExpandedInvoiceId(invoiceId);
    if (!invoiceItemsCache[invoiceId]) {
      setInvoiceItemsCache(prev => ({ ...prev, [invoiceId]: { loading: true, items: [] } }));
      try {
        const fullInvoice = await invoiceService.getInvoiceById(invoiceId);
        setInvoiceItemsCache(prev => ({
          ...prev,
          [invoiceId]: { loading: false, items: fullInvoice.items || [] },
        }));
      } catch {
        setInvoiceItemsCache(prev => ({ ...prev, [invoiceId]: { loading: false, items: [] } }));
      }
    }
  };

  // ── Quick Navigation ──────────────────────────────────────

  const handleQuickNav = async () => {
    const q = quickNavValue.trim();
    if (!q) return;
    try {
      setQuickNavLoading(true);
      setQuickNavError(null);
      const id = await invoiceService.lookupInvoice(q, quickNavCompanyId);
      navigate(`/invoices/${id}`);
    } catch (err) {
      setQuickNavError(err instanceof Error ? err.message : 'Nota fiscal não encontrada');
    } finally {
      setQuickNavLoading(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Success banner from navigation state */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center justify-between text-sm text-green-800">
          <span>{successMessage}</span>
          <button className="ml-4 text-green-600 hover:text-green-800 underline text-xs" onClick={() => setSuccessMessage(null)}>Fechar</button>
        </div>
      )}
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Notas Fiscais — Vendas</h1>
          <p className="text-sm text-slate-500 mt-1">
            {totalInvoices} {totalInvoices !== 1 ? 'notas fiscais' : 'nota fiscal'} de venda ao consumidor final
            {totalAmountSum !== undefined && (
              <span className="ml-2 font-semibold text-emerald-700">
                — Total: {formatCurrency(totalAmountSum)}
              </span>
            )}
          </p>
        </div>
        <div className="flex flex-col items-stretch sm:items-end gap-2">
          <div className="flex items-center gap-2">
            {/* Quick navigation — hidden on mobile, shown inline on sm+ */}
            <div className="hidden sm:flex items-center gap-1.5 bg-white border border-slate-200 rounded-lg px-2.5 h-9 shadow-sm">
              <select
                className="bg-transparent text-sm outline-none text-slate-600 max-w-[260px]"
                value={quickNavCompanyId ?? ''}
                onChange={e => setQuickNavCompanyId(e.target.value === '' ? undefined : Number(e.target.value))}
                title="Empresa (opcional)"
              >
                <option value="">Empresa...</option>
                {companies.map(c => (
                  <option key={c.company_id} value={c.company_id}>{c.name} — {formatCnpj(c.document)}</option>
                ))}
              </select>
              <span className="text-slate-300">|</span>
              <input
                type="text"
                className="bg-transparent text-sm outline-none w-44 placeholder:text-slate-400"
                placeholder="Nº ou chave de acesso..."
                value={quickNavValue}
                onChange={e => { setQuickNavValue(e.target.value); setQuickNavError(null); }}
                onKeyDown={e => e.key === 'Enter' && handleQuickNav()}
              />
              <button
                className="text-slate-500 hover:text-emerald-600 disabled:opacity-40 flex items-center"
                onClick={handleQuickNav}
                disabled={quickNavLoading || !quickNavValue.trim()}
                title="Ir para a nota fiscal"
              >
                {quickNavLoading
                  ? <Loader2 size={15} className="animate-spin" />
                  : <ChevronRight size={15} />}
              </button>
            </div>
            <Button variant="outline" icon={<RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />} onClick={handleManualRefresh} disabled={refreshing} title="Atualizar listagem">
              <span className="hidden sm:inline">Atualizar</span>
            </Button>
            <PermissionGuard permission="invoices.read">
              <Button
                variant="outline"
                icon={<Download size={16} />}
                disabled={xmlExporting || !filters.date_from || !filters.date_to}
                onClick={async () => {
                  setXmlExporting(true);
                  try {
                    await invoiceService.exportXmlZip(filters.date_from, filters.date_to, {
                      transaction_type: 'S',
                      final_customer: true,
                      status_ids: filters.status_ids.length > 0 ? filters.status_ids : undefined,
                      invoice_transaction_type_ids: filters.invoice_transaction_type_ids.length > 0 ? filters.invoice_transaction_type_ids : undefined,
                    });
                  } catch (err) {
                    setError(err instanceof Error ? err.message : 'Erro ao exportar XMLs');
                  } finally {
                    setXmlExporting(false);
                  }
                }}
                title="Exportar XMLs do período filtrado"
              >
                <span className="hidden sm:inline">{xmlExporting ? 'Exportando...' : 'Exportar XMLs'}</span>
              </Button>
            </PermissionGuard>
            <PermissionGuard permission="invoices.create">
              <Button icon={<Plus size={16} />} onClick={() => navigate('/invoices/new')}>
                Nova Nota Fiscal
              </Button>
            </PermissionGuard>
          </div>
          {quickNavError && (
            <p className="text-xs text-red-500">{quickNavError}</p>
          )}
          {/* Quick navigation — mobile only, shown below buttons */}
          <div className="sm:hidden">
            <div className="flex items-center bg-white border border-slate-200 rounded-lg shadow-sm h-9 overflow-hidden w-full">
              <select
                className="bg-slate-50 border-r border-slate-200 text-xs text-slate-600 px-2 h-full outline-none cursor-pointer hover:bg-slate-100 max-w-[130px]"
                value={quickNavCompanyId ?? ''}
                onChange={e => setQuickNavCompanyId(e.target.value === '' ? undefined : Number(e.target.value))}
                title="Empresa (opcional)"
              >
                <option value="">Empresa...</option>
                {companies.map(c => (
                  <option key={c.company_id} value={c.company_id}>{c.name}</option>
                ))}
              </select>
              <input
                type="text"
                className="bg-transparent text-sm outline-none px-2.5 flex-1 placeholder:text-slate-400"
                placeholder="Nº ou chave de acesso..."
                value={quickNavValue}
                onChange={e => { setQuickNavValue(e.target.value); setQuickNavError(null); }}
                onKeyDown={e => e.key === 'Enter' && handleQuickNav()}
              />
              <button
                className="text-slate-500 hover:text-emerald-600 disabled:opacity-40 flex items-center px-2 h-full border-l border-slate-200 hover:bg-slate-50"
                onClick={handleQuickNav}
                disabled={quickNavLoading || !quickNavValue.trim()}
                title="Ir para a nota fiscal"
              >
                {quickNavLoading
                  ? <Loader2 size={15} className="animate-spin" />
                  : <ChevronRight size={15} />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Search & filters */}
      <Card padding="none">
        <CardContent className="py-2 px-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input
                type="text"
                className="input pl-10 w-full"
                placeholder="Buscar por nome do cliente..."
                value={customerSearch}
                onChange={e => handleSearchChange(e.target.value)}
              />
            </div>
            <div className="relative">
              <Button
                variant="outline"
                data-filter-trigger
                icon={<Filter size={18} />}
                onClick={() => setShowFilters(!showFilters)}
              >
                Filtros
                {activeFilterCount > 0 && (
                  <span className="ml-1.5 bg-emerald-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {activeFilterCount}
                  </span>
                )}
                <ChevronDown size={16} className="ml-1" />
              </Button>

              {showFilters && (
                <div className="filter-dropdown absolute right-0 top-full mt-2 w-72 bg-white rounded-lg shadow-lg border border-slate-200 p-4 z-30 space-y-4">
                  {/* Empresa */}
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Empresa</label>
                    <select
                      className="input w-full text-sm"
                      value={filters.company_id !== undefined ? filters.company_id : ''}
                      onChange={e => {
                        const val = e.target.value;
                        setFilters(prev => ({ ...prev, company_id: val === '' ? undefined : Number(val) }));
                        setCurrentPage(1);
                      }}
                    >
                      <option value="">Todas</option>
                      {companies.map(c => (
                        <option key={c.company_id} value={c.company_id}>{c.name} — {formatCnpj(c.document)}</option>
                      ))}
                    </select>
                  </div>

                  {/* Modelo */}
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Modelo</label>
                    <select
                      className="input w-full text-sm"
                      value={filters.model !== undefined ? filters.model : ''}
                      onChange={e => {
                        const val = e.target.value;
                        setFilters(prev => ({ ...prev, model: val === '' ? undefined : Number(val) }));
                        setCurrentPage(1);
                      }}
                    >
                      <option value="">Todos</option>
                      {INVOICE_MODEL_OPTIONS.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Datas */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Data de</label>
                      <DateInputBR
                        className="input w-full text-sm"
                        value={filters.date_from}
                        onChange={v => { setFilters(prev => ({ ...prev, date_from: v })); setCurrentPage(1); }}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Data até</label>
                      <DateInputBR
                        className="input w-full text-sm"
                        value={filters.date_to}
                        onChange={v => { setFilters(prev => ({ ...prev, date_to: v })); setCurrentPage(1); }}
                      />
                    </div>
                  </div>

                  <button
                    className="text-xs text-emerald-600 hover:underline"
                    onClick={() => { setFilters({ company_id: undefined, status_ids: [], invoice_transaction_type_ids: [], model: undefined, date_from: getDefault3MonthsAgo(), date_to: getToday() }); setCurrentPage(1); }}
                  >
                    Limpar filtros
                  </button>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Status multi-select pills */}
      <Card padding="none">
        <CardContent className="py-3 px-4">
          <div className="flex items-center justify-between mb-2">
            <label className="block text-xs font-medium text-slate-600 uppercase tracking-wider">Situação da Nota Fiscal</label>
            <button
              onClick={toggleAllStatuses}
              className="text-xs text-emerald-600 hover:underline"
            >
              {filters.status_ids.length === INVOICE_STATUS_OPTIONS.length ? 'Desmarcar todos' : 'Marcar todos'}
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {INVOICE_STATUS_OPTIONS.map(opt => {
              const isSelected = filters.status_ids.includes(opt.value);
              return (
                <button
                  key={opt.value}
                  onClick={() => toggleStatus(opt.value)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all cursor-pointer select-none ${
                    isSelected
                      ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                      : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'
                  }`}
                >
                  {isSelected && <Check size={12} />}
                  {opt.label}
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Natureza da Operação multi-select pills */}
      {transactionTypes.length > 0 && (
        <Card padding="none">
          <CardContent className="py-3 px-4">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-xs font-medium text-slate-600 uppercase tracking-wider">Natureza da Operação</label>
              <button
                onClick={toggleAllTransactionTypes}
                className="text-xs text-emerald-600 hover:underline"
              >
                {filters.invoice_transaction_type_ids.length === transactionTypes.length ? 'Desmarcar todos' : 'Marcar todos'}
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {transactionTypes.map(tt => {
                const isSelected = filters.invoice_transaction_type_ids.includes(tt.invoice_transaction_type_id);
                return (
                  <button
                    key={tt.invoice_transaction_type_id}
                    onClick={() => toggleTransactionType(tt.invoice_transaction_type_id)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all cursor-pointer select-none ${
                      isSelected
                        ? 'bg-blue-50 border-blue-300 text-blue-700'
                        : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'
                    }`}
                  >
                    {isSelected && <Check size={12} />}
                    {tt.invoice_transaction_type_name}
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active filters strip */}
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-slate-500 text-xs font-medium">Filtros ativos:</span>
        <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 rounded-full px-3 py-0.5 text-xs">
          Período: {formatDate(filters.date_from)} — {formatDate(filters.date_to)}
          {(filters.date_from !== getDefault3MonthsAgo() || filters.date_to !== getToday()) && (
            <button
              className="ml-1 text-slate-400 hover:text-slate-600"
              onClick={() => { setFilters(prev => ({ ...prev, date_from: getDefault3MonthsAgo(), date_to: getToday() })); setCurrentPage(1); }}
              title="Resetar período"
            >×</button>
          )}
        </span>
        {filters.status_ids.length > 0 && (
          <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 rounded-full px-3 py-0.5 text-xs">
            Situações: {filters.status_ids.length} selecionada{filters.status_ids.length !== 1 ? 's' : ''}
            <button className="ml-1 text-emerald-400 hover:text-emerald-600" onClick={() => { setFilters(prev => ({ ...prev, status_ids: [] })); setCurrentPage(1); }} title="Remover">×</button>
          </span>
        )}
        {filters.invoice_transaction_type_ids.length > 0 && (
          <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 rounded-full px-3 py-0.5 text-xs">
            Naturezas: {filters.invoice_transaction_type_ids.length} selecionada{filters.invoice_transaction_type_ids.length !== 1 ? 's' : ''}
            <button className="ml-1 text-blue-400 hover:text-blue-600" onClick={() => { setFilters(prev => ({ ...prev, invoice_transaction_type_ids: [] })); setCurrentPage(1); }} title="Remover">×</button>
          </span>
        )}
        {filters.model !== undefined && (
          <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 rounded-full px-3 py-0.5 text-xs">
            Modelo: {INVOICE_MODEL_OPTIONS.find(o => o.value === filters.model)?.label ?? filters.model}
            <button className="ml-1 text-emerald-400 hover:text-emerald-600" onClick={() => { setFilters(prev => ({ ...prev, model: undefined })); setCurrentPage(1); }} title="Remover">×</button>
          </span>
        )}
        {filters.company_id !== undefined && (
          <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 rounded-full px-3 py-0.5 text-xs">
            Empresa: {companies.find(c => c.company_id === filters.company_id)?.name ?? filters.company_id}
            <button className="ml-1 text-emerald-400 hover:text-emerald-600" onClick={() => { setFilters(prev => ({ ...prev, company_id: undefined })); setCurrentPage(1); }} title="Remover">×</button>
          </span>
        )}
        {customerSearch.trim() && (
          <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 rounded-full px-3 py-0.5 text-xs">
            Busca: "{customerSearch.trim()}"
            <button className="ml-1 text-emerald-400 hover:text-emerald-600" onClick={() => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); setCustomerSearch(''); setSearchTerm(''); setCurrentPage(1); }} title="Remover">×</button>
          </span>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
          {error}
          <button className="ml-2 underline" onClick={() => setError(null)}>Fechar</button>
        </div>
      )}

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="animate-spin text-emerald-600" size={32} />
            </div>
          ) : invoices.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-slate-400">
              <FileText size={48} className="mb-3" />
              <p className="text-lg font-medium">Nenhuma nota fiscal encontrada</p>
              <p className="text-sm mt-1">Ajuste os filtros ou período de busca.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1200px] text-sm border-collapse">
                <TableHeader>
                  <tr>
                    <th className="py-3 px-2 w-8"></th>
                    <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">ID</th>
                    <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">Tipo</th>
                    <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">Número</th>
                    <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">Série</th>
                    <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">Modelo</th>
                    <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">Empresa</th>
                    <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">Data</th>
                    <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">Cliente</th>
                    <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">Cidade/UF</th>
                    <th className="py-3 px-4 text-right text-xs font-medium text-slate-500 uppercase">Total</th>
                    <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">Situação</th>
                    <th className="py-3 px-4 text-center text-xs font-medium text-slate-500 uppercase">Devolução</th>
                    <th className="py-3 px-4 text-center text-xs font-medium text-slate-500 uppercase">Ações</th>
                  </tr>
                </TableHeader>
                <tbody>
                  {invoices.map(invoice => (
                    <React.Fragment key={invoice.invoice_id}>
                      <TableRow>
                        <TableCell className="px-2 w-8">
                          <button
                            onClick={() => toggleExpand(invoice.invoice_id)}
                            className="p-1 rounded hover:bg-slate-200 transition-colors text-slate-400 hover:text-slate-600"
                            title={expandedInvoiceId === invoice.invoice_id ? 'Recolher itens' : 'Expandir itens'}
                          >
                            {expandedInvoiceId === invoice.invoice_id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                          </button>
                        </TableCell>
                        <TableCell className="font-medium text-slate-900">
                          <button
                            className="text-emerald-700 hover:underline font-semibold"
                            onClick={() => navigate(`/invoices/${invoice.invoice_id}`)}
                          >
                            {invoice.invoice_id}
                          </button>
                        </TableCell>
                        <TableCell>
                          {getOperationTypeBadge(invoice.type)}
                        </TableCell>
                        <TableCell className="text-slate-600 font-medium">
                          {invoice.number || '—'}
                        </TableCell>
                        <TableCell className="text-slate-600">
                          {invoice.serie || '—'}
                        </TableCell>
                        <TableCell className="text-slate-600 text-xs">
                          {getInvoiceModelLabel(invoice.model)}
                        </TableCell>
                        <TableCell>
                          {(() => {
                            const company = companies.find(c => c.company_id === invoice.company_id);
                            return company ? (
                              <div>
                                <span className="text-slate-800 text-xs font-medium">{company.name}</span>
                                <br />
                                <span className="text-slate-500 text-xs font-mono">{formatCnpj(company.document)}</span>
                              </div>
                            ) : (
                              <span className="text-slate-400">—</span>
                            );
                          })()}
                        </TableCell>
                        <TableCell>{formatDate(invoice.generation_timestamp)}</TableCell>
                        <TableCell>
                          <span className="text-slate-900">{invoice.customer_name || '—'}</span>
                        </TableCell>
                        <TableCell className="text-slate-600">
                          {invoice.ship_addr_city && invoice.ship_addr_state
                            ? `${invoice.ship_addr_city}/${invoice.ship_addr_state}`
                            : '—'}
                        </TableCell>
                        <TableCell className="text-right font-medium text-slate-800">
                          {formatCurrency(invoice.total_amount)}
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(invoice.status)}
                        </TableCell>
                        <TableCell className="text-center">
                          {invoice.return_invoice_id ? (
                            <button
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700 hover:bg-purple-200 transition-colors"
                              title={`Nota de devolução #${invoice.return_invoice_number || invoice.return_invoice_id}`}
                              onClick={() => navigate(`/invoices/${invoice.return_invoice_id}`)}
                            >
                              <RotateCcw size={12} />
                              NF #{invoice.return_invoice_number || invoice.return_invoice_id}
                              <ExternalLink size={10} />
                            </button>
                          ) : (
                            <span className="text-slate-300 text-xs">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            <PermissionGuard permission="invoices.read">
                              <button
                                className="p-1.5 text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 rounded transition-colors"
                                title="Visualizar"
                                onClick={() => navigate(`/invoices/${invoice.invoice_id}`)}
                              >
                                <Eye size={16} />
                              </button>
                            </PermissionGuard>
                            {/* Devolução: oferecida para toda NF de SAÍDA autorizada/emitida. Uma mesma venda pode
                                receber mais de uma devolução (devolução parcial em etapas), então uma devolução já
                                existente não esconde o botão. */}
                            {invoice.type !== 'E' && invoice.status && [5, 6].includes(invoice.status) && (
                              <PermissionGuard permission="invoices.create">
                                <button
                                  className="p-1.5 text-slate-500 hover:text-purple-600 hover:bg-purple-50 rounded transition-colors"
                                  title="Gerar nota de devolução"
                                  onClick={() => handleOpenReturnInvoice(invoice)}
                                >
                                  <RotateCcw size={16} />
                                </button>
                              </PermissionGuard>
                            )}
                            <PermissionGuard permission="invoices.read">
                              <button
                                className="p-1.5 text-slate-500 hover:text-amber-600 hover:bg-amber-50 rounded transition-colors"
                                title="Imprimir Etiqueta de Envio"
                                onClick={async () => {
                                  try {
                                    await invoiceService.downloadShippingLabel(invoice.invoice_id);
                                  } catch (err) {
                                    console.error('Erro ao gerar etiqueta:', err);
                                  }
                                }}
                              >
                                <Tag size={16} />
                              </button>
                            </PermissionGuard>
                          </div>
                        </TableCell>
                      </TableRow>

                      {/* Expanded items */}
                      {expandedInvoiceId === invoice.invoice_id && (
                        <tr>
                          <td colSpan={14} className="p-0 border-b border-slate-200">
                            {invoiceItemsCache[invoice.invoice_id]?.loading ? (
                              <div className="flex items-center gap-2 px-6 py-4 text-sm text-slate-500 bg-slate-50">
                                <Loader2 size={16} className="animate-spin text-emerald-600" />
                                Carregando itens...
                              </div>
                            ) : (
                              <div className="overflow-x-auto bg-emerald-50/20">
                                <table className="w-full text-sm border-collapse">
                                  <thead>
                                    <tr className="bg-slate-100 border-b border-slate-200">
                                      <th className="px-4 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Código</th>
                                      <th className="px-4 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Descrição</th>
                                      <th className="px-4 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">NCM</th>
                                      <th className="px-4 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">CFOP</th>
                                      <th className="px-4 py-2 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide">UN</th>
                                      <th className="px-4 py-2 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide">Qtd</th>
                                      <th className="px-4 py-2 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">Valor Unit.</th>
                                      <th className="px-4 py-2 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide">Total</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {(invoiceItemsCache[invoice.invoice_id]?.items ?? []).length > 0 ? (
                                      (invoiceItemsCache[invoice.invoice_id]?.items ?? []).map(item => (
                                        <tr key={item.invoice_item_id} className="bg-white hover:bg-emerald-50/60 border-b border-slate-100 transition-colors">
                                          <td className="px-4 py-2 font-mono font-semibold text-slate-800 text-xs whitespace-nowrap">{item.code || '—'}</td>
                                          <td className="px-4 py-2 text-slate-700 text-xs">{item.description || '—'}</td>
                                          <td className="px-4 py-2 text-slate-600 text-xs">{item.ncm || '—'}</td>
                                          <td className="px-4 py-2 text-slate-600 text-xs">{item.cfop || '—'}</td>
                                          <td className="px-4 py-2 text-center text-xs text-slate-700">{item.unit || '—'}</td>
                                          <td className="px-4 py-2 text-center text-xs text-slate-700">{item.quantity}</td>
                                          <td className="px-4 py-2 text-right text-xs text-slate-700 whitespace-nowrap">{formatCurrency(item.unit_value)}</td>
                                          <td className="px-4 py-2 text-right text-xs font-semibold text-emerald-700 whitespace-nowrap">
                                            {formatCurrency(item.total_value ?? item.unit_value * item.quantity)}
                                          </td>
                                        </tr>
                                      ))
                                    ) : (
                                      <tr>
                                        <td colSpan={8} className="px-4 py-4 text-center text-slate-400 text-sm italic">
                                          Nenhum item nesta nota fiscal
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 py-1">
        <p className="text-sm text-slate-500 order-2 sm:order-1">
          {totalInvoices > 0
            ? `${((currentPage - 1) * pageSize) + 1}–${Math.min(currentPage * pageSize, totalInvoices)} de ${totalInvoices} registro${totalInvoices !== 1 ? 's' : ''} · Página ${currentPage} de ${totalPages}`
            : 'Nenhum registro'}
        </p>
        {totalPages > 1 && (
          <div className="flex items-center gap-1 order-1 sm:order-2">
            <button
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage(1)}
              title="Primeira página"
              className="w-9 h-9 flex items-center justify-center rounded-md border text-sm font-medium border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >«</button>
            <button
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage(p => p - 1)}
              title="Página anterior"
              className="w-9 h-9 flex items-center justify-center rounded-md border text-sm font-medium border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >‹</button>
            {getPageRange(currentPage, totalPages).map((p, i) =>
              p === '...' ? (
                <span key={`ell-${i}`} className="w-9 h-9 flex items-center justify-center text-slate-400 text-sm">…</span>
              ) : (
                <button
                  key={p}
                  onClick={() => setCurrentPage(Number(p))}
                  title={`Página ${p}`}
                  className={`w-9 h-9 flex items-center justify-center rounded-md border text-sm font-medium transition-colors ${
                    currentPage === p
                      ? 'bg-emerald-600 text-white border-emerald-600'
                      : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                  }`}
                >{p}</button>
              )
            )}
            <button
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage(p => p + 1)}
              title="Próxima página"
              className="w-9 h-9 flex items-center justify-center rounded-md border text-sm font-medium border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >›</button>
            <button
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage(totalPages)}
              title="Última página"
              className="w-9 h-9 flex items-center justify-center rounded-md border text-sm font-medium border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >»</button>
          </div>
        )}
      </div>

      {/* Return Invoice Modal */}
      <ReturnInvoiceModal
        isOpen={returnInvoiceModalOpen}
        onClose={() => { setReturnInvoiceModalOpen(false); setReturnInvoiceSource(undefined); }}
        onSuccess={handleReturnInvoiceSuccess}
        sourceInvoice={returnInvoiceSource}
      />
    </div>
  );
};
